import React, { useEffect } from 'react';
import { AdvancedFilters, AdvancedFilterKey } from '../../interfaces/types';

interface FilterControlsProps {
  showAdvancedFilters: boolean;
  advancedFilters: AdvancedFilters;
  activeAdvancedFilterCount: number;
  filtersDropdownRef: React.RefObject<HTMLDivElement | null>;
  onToggleShowFilters: () => void;
  onToggleAdvancedFilter: (filterKey: AdvancedFilterKey) => void;
  onClearAll: () => void;
}

export const FilterControls: React.FC<FilterControlsProps> = ({
  showAdvancedFilters,
  advancedFilters,
  activeAdvancedFilterCount,
  filtersDropdownRef,
  onToggleShowFilters,
  onToggleAdvancedFilter,
  onClearAll,
}) => {
  // Handle clicks outside to close the advanced filters panel
  useEffect(() => {
    if (!showAdvancedFilters) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        filtersDropdownRef.current &&
        !filtersDropdownRef.current.contains(event.target as Node)
      ) {
        onToggleShowFilters();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdvancedFilters, onToggleShowFilters, filtersDropdownRef]);

  return (
    <div className="cost-centers-filter-dropdown" ref={filtersDropdownRef}>
      <button
        type="button"
        className={`cost-centers-filter-button ${
          showAdvancedFilters || activeAdvancedFilterCount > 0
            ? 'is-active'
            : ''
        }`}
        onClick={onToggleShowFilters}
        aria-expanded={showAdvancedFilters}
      >
        Filters
        <span className="cost-centers-filter-button__count">
          {activeAdvancedFilterCount || 0}
        </span>
      </button>

      {showAdvancedFilters ? (
        <div className="cost-centers-advanced-panel">
          <div className="cost-centers-advanced-panel__header">
            <span>Filters</span>
            <button
              type="button"
              className="cost-centers-clear-all"
              onClick={onClearAll}
            >
              Clear all
            </button>
          </div>
          <label className="cost-centers-checkbox">
            <input
              type="checkbox"
              checked={advancedFilters.hideNoUsers}
              onChange={() => onToggleAdvancedFilter('hideNoUsers')}
            />
            <span>Hide cost centers with no users</span>
          </label>
          <label className="cost-centers-checkbox">
            <input
              type="checkbox"
              checked={advancedFilters.hideNoBudget}
              onChange={() => onToggleAdvancedFilter('hideNoBudget')}
            />
            <span>Hide cost centers with no budget</span>
          </label>
          <label className="cost-centers-checkbox">
            <input
              type="checkbox"
              checked={advancedFilters.hideNoActivity}
              onChange={() => onToggleAdvancedFilter('hideNoActivity')}
            />
            <span>Hide cost centers with no activity</span>
          </label>
          <label className="cost-centers-checkbox">
            <input
              type="checkbox"
              checked={advancedFilters.overBudget}
              onChange={() => onToggleAdvancedFilter('overBudget')}
            />
            <span>Over budget</span>
          </label>
          <label className="cost-centers-checkbox">
            <input
              type="checkbox"
              checked={advancedFilters.highUtilization}
              onChange={() => onToggleAdvancedFilter('highUtilization')}
            />
            <span>High utilization (≥ 90%)</span>
          </label>
        </div>
      ) : null}
    </div>
  );
};
